import TinyColor from "@ctrl/tinycolor";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { PendingUpdate } from "@redotech/redo-agents-common/platform-agents/settings/pending-update";
import { CustomerPortalVersion } from "@redotech/redo-customer-portal-common/customer-portal-version";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { LocalizationProvider } from "@redotech/redo-i18n";
import { returnFlowJsonFormat } from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { isExchangeAgentEnabledForTeam } from "@redotech/redo-return-operations-common/return-flow/exchange-agent/exchange-agent-utils";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import { ReactNode, createContext, memo, useEffect } from "react";
import { getSettings } from "../api";

export const SettingsContext = createContext<
  CustomerPortalAppSettings | undefined
>(undefined);
export const StorefrontClientContext =
  createContext<ShopifyStorefrontClient | null>(null);
export const RefreshSettingsContext = createContext<
  (pendingUpdates?: PendingUpdate[]) => void
>(() => {});

export type SettingsOverrides = Partial<
  Omit<CustomerPortalAppSettings, "theme" | "warranties">
> & {
  theme?: Partial<CustomerPortalAppSettings["theme"]>;
  warranties?: Partial<CustomerPortalAppSettings["warranties"]>;
};

export const SettingsProvider = memo(function SettingsProvider({
  children,
  widgetId,
  overrides,
}: {
  children: ReactNode | ReactNode[];
  widgetId?: string;
  overrides?: SettingsOverrides;
}) {
  const [settingsLoad, refresh] = useTriggerLoadWithInput(
    async (pendingUpdates: PendingUpdate[] | undefined) => {
      const response = await getSettings(widgetId, pendingUpdates);
      const data = response.data;
      let storefrontClient: ShopifyStorefrontClient | null = null;
      if (data.storefrontAccessToken) {
        storefrontClient = new ShopifyStorefrontClient(
          data.storeUrl,
          data.storefrontAccessToken,
        );
      }
      const settings: CustomerPortalAppSettings = {
        theme: {
          button_background_color: data.theme.button_color,
          button_text_color:
            TinyColor(data.theme.button_color).getBrightness() < 200
              ? "#ffffff"
              : "#000000",
          button_color_dark:
            TinyColor(data.theme.button_color).getBrightness() < 200,
          accent_color: data.theme.accent_color,
          accent_background_color: data.theme.accent_background_color,
          accent_text_color:
            TinyColor(data.theme.accent_color).getBrightness() < 200
              ? "#ffffff"
              : "#000000",
          home_text_color: data.theme.home_text_color,
          background_image: data.theme.background_url || "",
          logo_image: data.settings.brandKit?.images.logoUrl || "",
          enable_alert: data.theme.enable_alert,
          notification: data.theme.notification,
          returnButtonText: data.theme.returnButtonText,
          registerButtonText: data.theme.registerButtonText,
          claimButtonText: data.theme.claimButtonText,
          standardExchangeText: data.theme.standard_exchange_text,
          instantExchangeText: data.theme.instant_exchange_text,
          exchangeButtonText: data.theme.exchangeButtonText,
          notExchangeButtonText: data.theme.notExchangeButtonText,
          notExchangeButtonSubtitle: data.theme.notExchangeButtonSubtitle,
          privacy_link: data.theme.privacy_link,
          body_font_family: data.theme.body_font_family,
          title_font_family: data.theme.title_font_family,
          custom_css: data.theme.custom_css,
          shippingFeeText: data.theme.shippingFeeText,
          custom_confirmation_text: data.theme.custom_confirmation_text,
          custom_payment_placeholder_color:
            data.theme.custom_payment_placeholder_color,
          green_return_text: data.theme.green_return_text,
          hideDiscountCodes: data.theme.hideDiscountCodes,
          hideSuggestedProducts: data.theme.hideSuggestedProducts,
          green_return_confirmation_text:
            data.theme.green_return_confirmation_text,
          green_return_confirmation_description:
            data.theme.green_return_confirmation_description,
          cantFindOrderText: data.theme.cantFindOrderText,
          claimSummarySubtext: data.theme.claimSummarySubtext,
          warrantySummarySubtext: data.theme.warrantySummarySubtext,
          returnSummarySubtext: data.theme.returnSummarySubtext,
          collapseVariantThreshold: data.theme.collapseVariantThreshold,
          merchantDisplayName: data.theme.merchantDisplayName,
          socialLoginOptions: data.theme.socialLoginOptions,
          backorderHeaderText: data.theme.backorderHeaderText,
          backorderSubtitleText: data.theme.backorderSubtitleText,
          backorderShowDate: data.theme.backorderShowDate,
        },
        customerEmail: data.customerEmail,
        merchantName: data.name,
        merchantAddress: data.address,
        redoCoverageProductIds: data.redoCoverageProductIds,
        discountDistributionMethod: data.settings.discountDistributionMethod,
        customInStoreAddresses: data.customInStoreAddresses,
        returns: data.settings.returns,
        withdrawals: {
          enabled: data.settings.withdrawals?.enabled ?? true,
          exclusion: data.settings.withdrawals?.exclusion,
        },
        pos: data.settings.pos,
        packageProtection: data.settings.packageProtection,
        warranties: data.settings.warranties,
        hasStorefrontAccess: data.hasStorefrontAccess,
        storefrontAccessToken: data.storefrontAccessToken || "",
        headlessAccessToken: data.headlessAccessToken,
        bigcommerceStorefront: data.bigcommerceStorefront,
        locations: data.settings.locations,
        returnInStoreEnabled: data.settings.returnInStoreEnabled,
        returnInStoreRadiusMiles: data.settings.returnInStoreRadiusMiles,
        customerLabelUpchargeFee: data.settings.customerLabelUpchargeFee,
        dontChargeCustomerLabelUpchargeWhenOnlyOption:
          data.settings.dontChargeCustomerLabelUpchargeWhenOnlyOption,
        inStoreDropOffPackingSlipEnabled:
          data.settings.inStoreDropOffPackingSlipEnabled,
        qrCodeOnlyShipping: data.settings.qrCodeOnlyShipping,
        customerAccounts: {
          ...data.settings.customerAccounts,
          enabled: data.settings.customerAccounts?.enabled ?? true,
          widgetSettings: {
            accountTabSections:
              data.settings.customerWidget?.customerAccounts
                ?.accountTabSections || [],
          },
        },
        storeUrl: data.storeUrl,
        exchanges: data.settings.exchanges,
        inventory: data.settings.inventory,
        shopifySubscription: data.settings.shopifySubscription,
        returnFlow: data.settings.returnFlow
          ? returnFlowJsonFormat.read(data.settings.returnFlow)
          : { steps: [], type: FlowType.RETURN },
        claimFlow: data.settings.claimFlow
          ? returnFlowJsonFormat.read(data.settings.claimFlow)
          : { steps: [], type: FlowType.CLAIM },
        finalizeReturnFlow: data.settings.finalizeReturnFlow
          ? returnFlowJsonFormat.read(data.settings.finalizeReturnFlow)
          : { steps: [], type: FlowType.FINALIZE_RETURN },
        finalizeClaimFlow: data.settings.finalizeClaimFlow
          ? returnFlowJsonFormat.read(data.settings.finalizeClaimFlow)
          : { steps: [], type: FlowType.FINALIZE_CLAIM },
        warrantyFlow: data.settings.warrantyFlow
          ? returnFlowJsonFormat.read(data.settings.warrantyFlow)
          : { steps: [], type: FlowType.WARRANTY },
        active: data.service_active,
        serviceDisabledReason: data.service_disabled_reason,
        returnAdjustment: data.settings.returnAdjustment,
        coverage: data.settings.coverage,
        merchantCoverage: data.settings.merchantCoverage,
        returnPortalHeaderText: data.settings.returnPortalHeaderText,
        returnPortalMissingOrderHeaderText:
          data.settings.returnPortalMissingOrderHeaderText,
        emailLoginLabelText: data.settings.emailLoginLabelText,
        orderNumberLoginLabelText: data.settings.orderNumberLoginLabelText,
        loginSubtitleText: data.settings.loginSubtitleText,
        registrationPortalTitle: data.settings.registrationPortalTitle,
        support: data.settings.support,
        showCustomerWidgetInReturnApp:
          !!data.settings.customerWidget?.general
            ?.showCustomerWidgetInReturnApp && !!data.settings.support,
        returnPortalButtonText: data.settings.returnPortalButtonText,
        returnPortalGiftButtonText: data.settings.returnPortalGiftButtonText,
        deductLabelFromCredit: data.settings.deductLabelFromCredit,
        deductLabelFromRefund: data.settings.deductLabelFromRefund,
        deductLabelFromExchange: data.settings.deductLabelFromExchange,
        hidePortalBranding: data.settings.hidePortalBranding,
        hidePoweredByInPortal: data.settings.hidePoweredByInPortal,
        exchangeGroups: data.settings.exchangeGroups,
        extendedWarranties: data.settings.extendedWarranties,
        variantExchangeTitle: data.settings.variantExchangeTitle,
        variantExchangeSubtitle: data.settings.variantExchangeSubtitle,
        exchangeGroupsExchangeTitle: data.settings.exchangeGroupsExchangeTitle,
        exchangeGroupsExchangeSubtitle:
          data.settings.exchangeGroupsExchangeSubtitle,
        sameItemTitle: data.settings.sameItemTitle,
        sameItemSubtitle: data.settings.sameItemSubtitle,
        newItemTitle: data.settings.newItemTitle,
        showLabelExpirationDate: data.settings.showLabelExpirationDate,
        labelExpiration: data.settings.labelExpiration,
        bundleRulesList: data.settings.bundleRulesList,
        instantRefundEnabled: data.settings.instantRefundEnabled,
        differentPricedVariantExchanges:
          data.settings.exchanges.differentPricedVariantExchanges,
        excludeDifferentPriceVariants:
          data.settings.exchanges.excludeDifferentPriceVariants ?? false,
        variantExchangeProductTags:
          data.settings.exchanges.variantExchangeProductTags,
        orderTracking: data.settings.orderTracking,
        returnTracking: data.settings.returnTracking,
        marketing: data.settings.marketing,
        resourceOverride: data.settings.resourceOverride,
        portalExcludedProductTags:
          data.settings.portalExcludedProductTags || [],
        portalExcludedProductIds: data.settings.portalExcludedProductIds || [],
        pickupEnabled: data.settings.pickupEnabled,
        exchangeOptionText: data.theme.exchangeOptionText,
        refundOptionText: data.theme.refundOptionText,
        enableNonShopifyClaims: !!data.settings.enableNonShopifyClaims,
        nonShopifyClaimsCollection: data.settings.nonShopifyClaimsCollection,
        nonShopifyClaimsDisableEditQuantity:
          data.settings.nonShopifyClaimsDisableEditQuantity,
        convertRecyclingProducts: data.settings.convertRecyclingProducts,
        recyclingProductId: data.settings.recyclingProductId,
        recycling: data.settings.recycling,
        hideManualReviewStep: data.settings.hideManualReviewStep,
        hideFeeInCompensationMethodModal:
          data.settings.hideFeeInCompensationMethodModal,
        hideSummaryCard: data.settings.hideSummaryCard,
        bulkReturnsEnabled: data.settings?.bulkReturnsEnabled,
        bulkReturns: {
          collections: data.settings?.returns?.bulkReturns?.collections ?? [],
          hideQuantity:
            data.settings?.returns?.bulkReturns?.hideQuantity ?? false,
        },
        loadAllAdvancedExchangeProducts:
          data.settings?.loadAllAdvancedExchangeProducts,
        hideGiftOption: data.settings.hideGiftOption,
        claimReviewHeader: data.theme.claimReviewHeader,
        claimShippingLineItemText: data.theme.claimShippingLineItemText,
        automation: data.automation,
        exchangeAgentSettings: {
          ...data.settings.exchangeAgent,
          // Override enabled field with the combined exchange agent billing settings and merchant settings
          enabled: isExchangeAgentEnabledForTeam(
            data.settings.returnsAiExchange,
            data.settings.exchangeAgent,
            data,
          ),
        },
        customerPortalVersion:
          data.settings.customerPortalVersion ?? CustomerPortalVersion.V4_0,
        bundles: data.settings.bundles,
        brandKit: data.settings.brandKit,
        packagePickupVariants: data.settings.packagePickupVariants,
        useShopifyStorefrontForCommentSold:
          data.settings?.commentsold?.useShopifyStorefront ?? false,
        dontChargeMerchantForDamagedOrWrongProductReturnLabels:
          data.settings.returns
            ?.dontChargeMerchantForDamagedOrWrongProductReturnLabels ?? false,
        variantPickerType: data.settings.exchanges.variantPickerType,
        variantPickerColorHexMetafield:
          data.settings.exchanges.variantPickerColorHexMetafield ?? undefined,
        orderEditing: data.settings.orderEditing,
        newOmsEnabled: data.settings.outboundLabels?.newOmsEnabled,
        minRecyclingProductQuantity:
          data.settings.recycling?.minRecyclingProductQuantity,
        localization: data.settings.localization,
        loadingBackgroundColor: data.loadingBackgroundColor,
        hideTrackingPageNavigation: data.hideTrackingPageNavigation,
        hideTrackingPageNavbar: data.hideTrackingPageNavbar,
        featureFlags: data.featureFlags,
      };
      return { storefrontClient, settings };
    },
  );

  // Trigger initial load on mount
  useEffect(() => {
    refresh(undefined);
  }, [refresh]);

  const settings: CustomerPortalAppSettings | undefined = settingsLoad.value
    ?.settings
    ? {
        ...settingsLoad.value.settings,
        ...overrides,
        theme: { ...settingsLoad.value.settings.theme, ...overrides?.theme },
        warranties: settingsLoad.value.settings.warranties
          ? {
              ...settingsLoad.value.settings.warranties,
              ...overrides?.warranties,
            }
          : settingsLoad.value.settings.warranties,
      }
    : undefined;
  const storefrontClient = settingsLoad.value?.storefrontClient;

  if (!settings) {
    return null;
  }

  return (
    <LocalizationProvider localization={settings.localization}>
      <RefreshSettingsContext.Provider value={refresh}>
        <SettingsContext.Provider value={settings}>
          <StorefrontClientContext.Provider value={storefrontClient ?? null}>
            {children}
          </StorefrontClientContext.Provider>
        </SettingsContext.Provider>
      </RefreshSettingsContext.Provider>
    </LocalizationProvider>
  );
});
